import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  BankCardApiService,
  CardZonesEnum,
  RegisterCardDataInterface,
  RegisterCardResponse,
} from '@client-monorepo/daily-fintech/bank-card';
import { ShaparakCardInfo, ShaparakConfig, ShaparakTypes } from '../models/shaparak.model';
import { C2cSourceCardService } from './c2c-source-card.service';
import { getCardPanPostfix, getCardPanPrefix, MessageService } from '@client-monorepo/common/utilities';
import { JSEncrypt } from 'jsencrypt';
import { CardRegistration } from '../models/card-registration.enum';
import { PaymentUrlService } from '@client-monorepo/payment/purchase';

@Injectable({
  providedIn: 'root',
})
export class ShaparakService {
  shaparakConfig = new BehaviorSubject<ShaparakConfig | null>(null);

  shaparakType = new BehaviorSubject<ShaparakTypes | null>(null);

  constructor(
    private messageService: MessageService,
    private c2cSourceCardService: C2cSourceCardService,
    private bankCardApiService: BankCardApiService,
    private paymentUrlService: PaymentUrlService,
  ) {}

  /**
   * register new card for source or destination card
   */
  registerNewCard(cardNumber: string, cardZone: number, targetSide: boolean, cardOwner: string): Promise<RegisterCardResponse> {
    this.setCallbackUrlFromShaparak();
    const prefix = getCardPanPrefix(cardNumber);
    const postfix = getCardPanPostfix(cardNumber);
    const pan = cardNumber;

    return new Promise((resolve, reject) => {
      this.c2cSourceCardService.getVaultCertFile().then((certText) => {
        const encrypt = new JSEncrypt();
        encrypt.setPublicKey(certText);
        const encryptedPan = encrypt.encrypt(pan) as string;
        const callbackUrl = this.shaparakConfig.getValue()?.callbackUrl;

        const body: RegisterCardDataInterface = {
          prefix,
          postfix,
          cardOwner,
          encryptedPan,
          targetSide,
          callbackUrl,
        };
        this.bankCardApiService.registerNewCard(body, cardZone).subscribe(
          (registerCard) => {
            if (!targetSide) {
              if (registerCard.cardInfo.cardIndex) {
                this.c2cSourceCardService.sourceCardProfile.getValue()!.cardIndex = registerCard.cardInfo.cardIndex;
              }
              if (registerCard.cardInfo.expireDate) {
                this.c2cSourceCardService.sourceCardProfile.getValue()!.expirationDate = registerCard.cardInfo.expireDate;
              }
            }
            resolve(registerCard);
          },
          (error) => {
            reject(error);
            this.messageService.showErrorOfErrorResponse(error, 'بروز خطا! لطفا مجددا تلاش کنید');
          },
        );
      });
    });
  }

  private registerCardInShaparak(pan: string, cardZone: CardZonesEnum, cardHolder: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.registerNewCard(pan, cardZone, false, cardHolder)
        .then((result) => {
          if (result.redirectUrl) {
            resolve(result.redirectUrl);
          }
        })
        .catch((e) => {
          this.messageService.showErrorOfErrorResponse(e);
          reject(e);
        });
    });
  }

  setCallbackUrlFromShaparak(callbackUrl = '') {
    const baseCallbackUrl = 'service/c2c/shaparak/';
    const config: ShaparakConfig = { ...this.shaparakConfig.getValue() } as ShaparakConfig;
    if (callbackUrl) {
      config.callbackUrl = this.paymentUrlService.appCallbackUrl(`${baseCallbackUrl}${callbackUrl}`, true);
    } else {
      config.callbackUrl = this.paymentUrlService.appCallbackUrl(`${baseCallbackUrl}register/0`, true);
    }
    this.shaparakConfig.next(config);
  }

  async setShaparakUrl({ cardInfo, url = '' }: { cardInfo: ShaparakCardInfo | null; url: string }) {
    if (this.shaparakConfig.getValue()?.redirectUrl) {
      return;
    }
    const config: ShaparakConfig = { ...this.shaparakConfig.getValue() } as ShaparakConfig;
    switch (this.shaparakType.getValue()) {
      case ShaparakTypes.EXPIRATION_DATE:
        config.redirectUrl = url;
        break;
      case ShaparakTypes.REGISTER:
        await this.registerCardInShaparak(cardInfo?.pan || '', CardZonesEnum.external, cardInfo?.cardHolder || '').then(
          (res) => (config.redirectUrl = res),
        );
        break;
      default:
        await this.registerCardInShaparak(cardInfo?.pan || '', CardZonesEnum.external, cardInfo?.cardHolder || '').then(
          (res) => (config.redirectUrl = res),
        );
        break;
    }
    await this.shaparakConfig.next(config);
  }

  setShaparakConfig(card: ShaparakCardInfo) {
    const config: ShaparakConfig = { ...this.shaparakConfig.getValue() } as ShaparakConfig;
    switch (this.shaparakType.getValue()) {
      case ShaparakTypes.EXPIRATION_DATE:
        config.forcible = card.cardZones?.includes(CardZonesEnum.external) as boolean;
        config.description = 'برای انتقال وجه تغییر تاریخ انقضا باید در سامانه شاپرک ثبت شود.';
        break;
      case ShaparakTypes.REGISTER:
        config.forcible = card.cardExternalRegistrationMode === CardRegistration.FORCE;
        config.description = this.c2cSourceCardService.externalRegisterDescription.getValue();
        break;
      default:
        config.forcible = card.cardExternalRegistrationMode === CardRegistration.FORCE;
        config.description = this.c2cSourceCardService.externalRegisterDescription.getValue();
        break;
    }
    this.shaparakConfig.next(config);
  }

  resetShaparkConfig() {
    this.shaparakConfig.next(null);
    this.shaparakType.next(null);
  }
}
